import * as React from "react";
import { cn } from "@/lib/utils";
import { SpotlightCard, SpotlightCardContent } from "./SpotlightCard";
import { SectionLabel } from "./GradientText";
import { Quote } from "lucide-react";

interface Testimonial {
  id: string;
  quote: string;
  author: string;
  role: string;
  avatar?: string;
}

interface TestimonialsProps {
  title: string;
  subtitle?: string;
  testimonials: Testimonial[];
}

// Rotating accent colors for testimonial cards
const accentColors = ["yellow", "pink", "blue"] as const;
const avatarColors = ["#FFDC00", "#FF73A9", "#3F5EF4", "#00EB90"];

/**
 * Testimonials (Neo-Brutalist Version)
 *
 * Grid of testimonial cards with:
 * - Rotating accent color shadows
 * - Bold quote icons
 * - Hard shadows and lift-on-hover
 * - Staggered entrance animations
 */
export function Testimonials({
  title,
  subtitle,
  testimonials,
}: TestimonialsProps) {
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="testimonials"
      className="py-24 lg:py-32 bg-[#FFFBF0]"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div
          className={cn(
            "text-center max-w-3xl mx-auto mb-16 lg:mb-20",
            "transition-all duration-500 ease-out",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
        >
          <SectionLabel color="pink" className="mb-6">
            Testimonials
          </SectionLabel>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-4 font-['Epilogue'] text-[#1A1A1A]">
            {title}
          </h2>

          {subtitle && (
            <p className="text-[#666666] text-base lg:text-lg font-['Sora']">
              {subtitle}
            </p>
          )}
        </div>

        {/* Testimonials grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => {
            const accentColor = accentColors[index % accentColors.length];
            const avatarColor = avatarColors[index % avatarColors.length];

            return (
              <SpotlightCard
                key={testimonial.id}
                accentColor={accentColor}
                className={cn(
                  "transition-all duration-300 ease-out",
                  isVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-8"
                )}
                style={{
                  transitionDelay: isVisible ? `${index * 80}ms` : "0ms",
                }}
              >
                <SpotlightCardContent className="h-full flex flex-col p-6">
                  {/* Quote icon */}
                  <div
                    className={cn(
                      "w-10 h-10 rounded-lg flex items-center justify-center mb-4",
                      "bg-[#FFDC00]",
                      "border-2 border-black",
                      "shadow-[2px_2px_0_0_#000000]"
                    )}
                  >
                    <Quote className="w-5 h-5 text-black" />
                  </div>

                  {/* Quote text */}
                  <blockquote className="text-[#1A1A1A] text-base leading-relaxed mb-6 flex-1 font-['Sora']">
                    "{testimonial.quote}"
                  </blockquote>

                  {/* Author */}
                  <div className="flex items-center gap-3">
                    {/* Avatar */}
                    <div
                      className={cn(
                        "w-12 h-12 rounded-xl overflow-hidden",
                        "border-2 border-black",
                        "flex items-center justify-center"
                      )}
                      style={{ backgroundColor: avatarColor }}
                    >
                      {testimonial.avatar ? (
                        <img
                          src={testimonial.avatar}
                          alt={testimonial.author}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // Fallback to initials on error
                            const target = e.target as HTMLImageElement;
                            target.style.display = "none";
                          }}
                        />
                      ) : (
                        <span className="text-black font-bold text-sm font-['Epilogue']">
                          {testimonial.author
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}
                        </span>
                      )}
                    </div>

                    {/* Name & Role */}
                    <div>
                      <div className="font-bold text-[#1A1A1A] text-sm font-['Epilogue']">
                        {testimonial.author}
                      </div>
                      <div className="text-[#666666] text-xs font-['Sora']">
                        {testimonial.role}
                      </div>
                    </div>
                  </div>
                </SpotlightCardContent>
              </SpotlightCard>
            );
          })}
        </div>
      </div>
    </section>
  );
}
